/**
 * Pure decision helpers for the data-room cloud sync flow.
 *
 * Extracted from the main data-room screen so the integrity-critical logic is
 * unit-tested independently of the UI. See sync-helpers.test.ts.
 */

export type DataRoomProvider = 'google' | 'dropbox' | 'docsend';

/**
 * The folder id to sync for a provider: the founder's persisted selection, else
 * the provider's root. Google root is the literal 'root'; Dropbox root is ''.
 */
export function resolveSyncFolderId(
  provider: DataRoomProvider,
  persistedFolderId: string | null | undefined,
): string {
  if (persistedFolderId) return persistedFolderId;
  return provider === 'google' ? 'root' : '';
}

export type SyncOutcome =
  | { kind: 'files' } // real files were fetched → classify them
  | { kind: 'mock' } // mock/demo mode, no real files → use sample data
  | { kind: 'reconnect' } // production, token failed → prompt reconnect (never fake docs)
  | { kind: 'empty' }; // production, connected, folder genuinely empty

/**
 * Decide what a sync attempt should do given the result.
 *
 * The load-bearing rule: in production (`showSampleData === false`) we NEVER
 * substitute mock documents — a token failure surfaces a reconnect prompt, and a
 * genuinely empty folder reports empty. Mock/demo mode keeps its sample data
 * (and that takes precedence over an auth failure, so demo never nags reconnect).
 */
export function resolveSyncOutcome(input: {
  fileCount: number;
  authFailed: boolean;
  showSampleData: boolean;
}): SyncOutcome {
  if (input.fileCount > 0) return { kind: 'files' };
  if (input.showSampleData) return { kind: 'mock' };
  if (input.authFailed) return { kind: 'reconnect' };
  return { kind: 'empty' };
}
